import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";
import PageSEO from "@/components/feature/PageSEO";

const faqs: { q: string; a: React.ReactNode }[] = [
  {
    q: "¿Cuánto cuesta el vaciado de un piso en Girona?",
    a: (
      <>
        El precio depende del volumen, el estado del inmueble y el tipo de residuos. Ofrecemos{" "}
        <Link to="/contacto" className="text-[#ff0000] hover:underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
          presupuesto sin compromiso
        </Link>{" "}
        tras una valoración por fotos o visita gratuita. El servicio mínimo equivale a una furgoneta completa. No cobramos por desplazamiento en Girona ciudad y comarca.
      </>
    ),
  },
  {
    q: "¿Cuánto tiempo tarda un vaciado de piso?",
    a: (
      <>
        Un piso estándar de 3 habitaciones suele vaciarse en un solo día. Los casos más complejos, como{" "}
        <Link to="/servicios#limpieza-diogenes" className="text-[#ff0000] hover:underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
          limpiezas por síndrome de Diógenes
        </Link>{" "}
        o pisos con mucho volumen, pueden requerir 2-3 días. Te damos una estimación exacta en el presupuesto.
      </>
    ),
  },
  {
    q: "¿Qué hacéis con los residuos y muebles retirados?",
    a: (
      <>
        Gestionamos los residuos de forma responsable y sostenible: lo que está en buen estado se dona a entidades sociales o se recicla, y el resto se lleva a puntos de reciclaje autorizados cumpliendo la normativa vigente de gestión de residuos de Catalunya. Puedes ver más detalles en nuestra página de{" "}
        <Link to="/empresa" className="text-[#ff0000] hover:underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
          valores y compromiso medioambiental
        </Link>.
      </>
    ),
  },
  {
    q: "¿Ofrecéis servicio urgente?",
    a: (
      <>
        Sí, disponemos de servicio urgente para casos que requieren actuación rápida: entregas de local, herencias con plazos, situaciones de emergencia.{" "}
        <Link to="/contacto" className="text-[#ff0000] hover:underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
          Contáctanos
        </Link>{" "}
        por teléfono o WhatsApp y valoramos tu situación. En muchos casos podemos actuar en 24-48 horas.
      </>
    ),
  },
  {
    q: "¿El servicio es discreto? ¿Se enterarán los vecinos?",
    a: (
      <>
        Trabajamos con total discreción. En casos sensibles como el{" "}
        <Link to="/servicios#limpieza-diogenes" className="text-[#ff0000] hover:underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
          síndrome de Diógenes
        </Link>{" "}
        o situaciones delicadas, coordinamos los accesos y horarios para minimizar la visibilidad. Nuestro equipo actúa con profesionalidad y respeto en todo momento.
      </>
    ),
  },
  {
    q: "¿Recogéis muebles sueltos?",
    a: (
      <>
        No ofrecemos recogida de muebles o electrodomésticos sueltos. El servicio mínimo es una furgoneta completa. Si tienes varios muebles o electrodomésticos que retirar, consulta nuestro{" "}
        <Link to="/servicios#retirada-muebles" className="text-[#ff0000] hover:underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
          servicio de retirada de muebles y electrodomésticos
        </Link>{" "}
        o contáctanos para valorar tu caso.
      </>
    ),
  },
  {
    q: "¿Trabajáis fuera de Girona ciudad?",
    a: (
      <>
        Sí, cubrimos toda la comarca del Gironès y poblaciones cercanas: Salt, Sarrià de Ter, Quart, Vilablareix, Fornells de la Selva, Bescanó, Celrà, Cassà de la Selva y Banyoles. Si tienes dudas sobre tu municipio,{" "}
        <Link to="/contacto" className="text-[#ff0000] hover:underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
          consúltanos sin compromiso
        </Link>.
      </>
    ),
  },
  {
    q: "¿Puedo pedir presupuesto por fotos sin visita?",
    a: (
      <>
        Sí, en la mayoría de casos podemos darte un presupuesto orientativo por fotos o vídeo. Para casos complejos o de gran volumen, recomendamos una visita gratuita sin compromiso para darte el precio más ajustado posible.{" "}
        <Link to="/contacto" className="text-[#ff0000] hover:underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
          Solicita tu presupuesto aquí
        </Link>.
      </>
    ),
  },
];

function FadeIn({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}>
      {children}
    </div>
  );
}

export default function FaqPage() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <PageSEO
        title="Preguntas Frecuentes sobre Vaciado de Pisos en Girona | GIROBUIT"
        description="Resolvemos tus dudas sobre el vaciado de pisos y locales en Girona: precios, plazos, gestión de residuos, discreción y servicio urgente. GIROBUIT."
        canonical="https://girobuit.com/faq"
        pageType="FAQPage"
        breadcrumbs={[
          { name: "Inicio", url: "https://girobuit.com/" },
          { name: "Preguntas frecuentes", url: "https://girobuit.com/faq" },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-36 md:pt-40 pb-16 bg-[#330000] text-center px-4">
        <nav className="text-xs text-red-300 mb-4">
          <Link to="/" className="hover:text-white cursor-pointer">Inicio</Link>
          <span className="mx-2">/</span>
          <span className="text-white">Preguntas frecuentes</span>
        </nav>
        <h1 className="text-4xl md:text-5xl font-900 text-white mb-4" style={{ fontWeight: 900 }}>
          Preguntas frecuentes
        </h1>
        <p className="text-red-200 max-w-xl mx-auto text-base leading-relaxed">
          Resolvemos las dudas más habituales sobre el vaciado de pisos y locales en Girona. Si no encuentras tu respuesta, llámanos.
        </p>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl md:text-3xl font-800 text-[#330000] mb-8 text-center" style={{ fontWeight: 800 }}>
            Dudas sobre el vaciado de pisos en Girona
          </h2>
          <div className="flex flex-col gap-4">
            {faqs.map((faq, i) => (
              <FadeIn key={i}>
                <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,0,0,0.12)" }}>
                  <button
                    onClick={() => setOpen(open === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer bg-white hover:bg-[#ff0000]/5 transition-colors duration-200"
                    aria-expanded={open === i}
                  >
                    <h3 className="text-base font-700 text-[#330000] m-0" style={{ fontWeight: 700 }}>{faq.q}</h3>
                    <i className={`ri-add-line text-xl text-[#ff0000] flex-shrink-0 transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}></i>
                  </button>
                  {open === i && (
                    <div className="px-6 pb-5 bg-[#f9f5f5]">
                      <p className="text-sm text-gray-600 leading-relaxed pt-3">{faq.a}</p>
                    </div>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#FF6666]">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-800 text-[#330000] mb-3" style={{ fontWeight: 800 }}>¿No encuentras tu respuesta?</h2>
          <p className="text-[#330000]/80 mb-8">Llámanos o escríbenos por WhatsApp. Atendemos en Girona y toda la comarca del Gironès.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+34602365996" className="flex items-center justify-center gap-2 bg-[#330000] text-white px-8 py-4 rounded-full text-sm font-700 whitespace-nowrap cursor-pointer hover:bg-[#ff0000] transition-colors duration-300" style={{ fontWeight: 700 }}>
              <i className="ri-phone-fill"></i> 602 36 59 96
            </a>
            <Link to="/contacto" className="flex items-center justify-center gap-2 border-2 border-[#330000] text-[#330000] px-8 py-4 rounded-full text-sm font-700 whitespace-nowrap cursor-pointer hover:bg-[#330000] hover:text-white transition-all duration-300" style={{ fontWeight: 700 }}>
              <i className="ri-mail-line"></i> Formulario de contacto
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
